import { useEffect, useRef, useState } from 'react';
import { createWorker } from 'tesseract.js';
// eslint-disable-next-line import/no-webpack-loader-syntax
import { preprocessImage } from './preprocessImage';

const useWorkers = (onMessage, jobQueueLengthRef, numWorkers = 4, transformations = []) => {
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    const workersRef = useRef([]);
    const [jobQueue, setJobQueue] = useState([]);
    const timerRef = useRef(null);
    const jobIdRef = useRef(0); // Ref to keep track of unique job IDs
    const jobTimeout = 5000; // 2 seconds in milliseconds
    const [processedImage, setProcessedImage] = useState(null);

    useEffect(() => {
        // Initialize workers if not already initialized
        if (workersRef.current.length === 0) {

            const initializeWorkers = async () => {
                console.log("Initializing OCR workers...");
                const workersPromises = Array.from({ length: numWorkers }, async () => {
                    const TheWorker = await createWorker('eng', 1); // Create worker with LSTM engine

                    await TheWorker.setParameters({
                        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789- .',
                        // tessedit_pageseg_mode: 8
                    });

                    return TheWorker;

                });

                workersRef.current = await Promise.all(workersPromises);
            }
            initializeWorkers();
        }


        return () => {
            // Cleanup workers on component unmount
            console.log("Terminating workers...");
            workersRef.current.forEach(workerInstance => {
                if (workerInstance) {
                    workerInstance.terminate();
                }
            });
            workersRef.current = []; // Clear the reference to avoid memory leaks
        };
    }, [numWorkers]);


    const handleNextJob = async () => {
        setJobQueue(prevQueue => {
            if (prevQueue.length === 0) return prevQueue;

            const nextJob = prevQueue[prevQueue.length - 1]; // Get the job added as last in the queue
            setProcessedImage(nextJob.imageUrl);
            const workerIndex = nextJob.id % numWorkers;


            if (workersRef.current[workerIndex]) {
                const workerInstance = workersRef.current[workerIndex];

                if (!workerInstance) {
                    console.log("Worker instance not found.");
                    return prevQueue;
                }


                workerInstance.recognize(nextJob.imageUrl).then(({ data: { text } }) => {
                    const message = { status: 'done', text, mode: nextJob.mode, id: nextJob.id };
                    console.log(message)
                    onMessageRef.current(message);
                    removeJobById(nextJob.id);
                })
                    .catch(error => {
                        onMessageRef.current({ status: 'error', error: error.message });
                        removeJobById(nextJob.id);
                    });
            }


            return prevQueue; // filter out old jobs
        });
    };


    const removeJobById = (jobId) => {
        setJobQueue(prevQueue => {
            const newQueue = prevQueue.filter(job => job.id !== jobId);
            jobQueueLengthRef.current = newQueue.length;
            return newQueue
        });
    };

    useEffect(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        timerRef.current = setInterval(() => {
            if (jobQueueLengthRef.current > 0 && workersRef.current.length > 0) {
                handleNextJob();
            }
        }, 250);

        return () => {
            clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addJob = async (job) => {
        const [image, canvas] = await preprocessImage(job.screenshot, transformations); // Wait for processing to complete
        const imageUrl = canvas.toDataURL();

        const now = Date.now();
        if (image) {
            const jobWithId = {
                ...job,
                id: jobIdRef.current++, // Add unique ID
                timestamp: now,   // Add timestamp
                processedImage: image,// Include the processed image data
                imageUrl,
            };

            setJobQueue(prevQueue => {
                const newQuery = [...prevQueue.filter(i => (now - i.timestamp) < jobTimeout), jobWithId]; // Add the processed job to the queue
                jobQueueLengthRef.current = newQuery.length; // Update job queue length
                return newQuery
            });
        } else {
            console.error("Failed to process the image.");
        }
    };

    return { addJob, jobQueue, jobQueueLengthRef, processedImage };
};

export default useWorkers;
